
import ApplyGiftCards from '@/checkout/components/ApplyGiftCards.vue'
import StripeCreditCard from '@/checkout/components/StripeCreditCard.vue'
import { walletNames, WalletType } from '@/checkout/helpers/wallets'
import FormInput2 from '@/components/forms/FormInput2.vue'
import { logInTrackJS } from '@/errors/helpers'
import { addressFieldNames } from '@/helpers/AddressHelpers'
import environment from '@/helpers/Environment'
import { configIdentityFields } from '@/helpers/IdentityHelpers'
import type { LanguageStrings } from '@/language/types'
import type { Stripe, StripeCardElement } from '@stripe/stripe-js'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

interface PaymentOption {
  value: PaymentMethodIdentifier
  label: string
}

export type PaymentMethodIdentifier = 'stripe_card' | 'stripe_wallet' | 'stripe_afterpay' // PayPal etc

@Component({
  name: 'SelectPaymentMethod',
  components: { FormInput2, StripeCreditCard, ApplyGiftCards },
  computed: mapGetters({
    // TODO Replace with props.
    cart: 'Cart/cart',
    paymentDue: 'Cart/paymentDue',
    paymentDueByCreditCard: 'Cart/paymentDueByCreditCard',
  }),
})
export default class extends Vue {
  @Prop({ required: true })
  value: PaymentMethodIdentifier

  @Prop({ default: false })
  processing: boolean

  @Prop({ required: true })
  wallet: WalletType

  @Prop({ required: true })
  stripeCardElement: StripeCardElement | null

  @Prop({ required: true })
  isModifiable: boolean

  @Prop({ required: true })
  stripe: Stripe

  @Prop({ required: true })
  validAfterpayCart: boolean

  // Mapped VueX computed properties.
  cart: CartEntity
  paymentDueByCreditCard: number

  // Options plugin properties.
  t: LanguageStrings['selectPaymentMethod']

  get paymentTypeOptions(): PaymentOption[] {
    const result: PaymentOption[] = []

    // Apple Pay requires that it is the first/primary payment method available if it is supported.
    if (this.wallet !== 'none') {
      result.push({ value: 'stripe_wallet', label: walletNames[this.wallet] })
    }

    result.push({ value: 'stripe_card', label: 'Credit or debit card' })

    if (this.afterpayEnabled) {
      result.push({ value: 'stripe_afterpay', label: 'Afterpay' })
    }

    return result
  }

  get afterpayEnabled() {
    if (environment.web.additional_payment_methods_enabled?.afterpay && this.validAfterpayCart) {
      // Check that definitions exist for all the address fields required by Afterpay in `config.custom_identity_fields`.
      const config = configIdentityFields()
      const result = addressFieldNames.every((name) => config[name] != undefined)
      if (!result) {
        logInTrackJS('Afterpay: Missing address field definition', config)
      }

      return result
    }

    return false
  }

  @Watch('afterpayEnabled')
  onAfterpayEnabledChanged() {
    if (!this.afterpayEnabled && this.value === 'stripe_afterpay') {
      // Update selected payment method when adding checkout donations to cart as donations aren't allowed by afterpay.
      this.$emit('input', this.paymentTypeOptions[0])
    }
  }

  get giftCardsEnabled() {
    return this.isModifiable && Boolean(environment.web.additional_payment_methods_enabled?.gift_card)
  }
}
